import blobStorageServices, {
  BlobStorageServiceType,
} from "./blobStorageService";
import conversationServices, {
  ConversationServiceType,
} from "./conversationServices";
import lawTermServices, { LawTermServiceType } from "./lawTermService";
import legislationServices, {
  LegislationServiceType,
} from "./legislationServices";
import messageServices, { MessageServiceType } from "./messageService";
import negotiateServices, { NetgotiateServiceType } from "./negotiateService";
import promptServices, { PromptServiceType } from "./promptServices";
import { RestClient, restClientAuth0 } from "./restClient";
import statisticServices, { StatisticServiceType } from "./statisticService";
import tagServices, { TagServiceType } from "./tagServices";
import tenantServices, { TenantServiceType } from "./tenantServices";
import userServices, { UserServiceType } from "./userServices";
import workspaceServices, { WorkspaceServiceType } from "./workspaceServices";
import commentServices, { CommentServiceType } from "./commentServices";
import groupServices, { GroupServiceType } from "./groupService";
import featureFlagServices, {
  FeatureFlagServiceType,
} from "./featureFlagServices";
import toneOfVoiceService, {
  ToneOfVoiceServiceType,
} from "./toneOfVoiceService";
import searchServices, { SearchServiceType } from "./searchServices";
import legislationFilterServices, {
  LegislationFilterServiceType,
} from "./legislationFilterService";
import userManagementServices, {
  UserManagementServiceType,
} from "./userManagementService";
import documentLinkService, {
  DocumentLinkServiceType,
} from "./documentLinkService";
import synonymGroupServices, {
  SynonymGroupServiceType,
} from "./synonymGroupService";
import userPreferenceServices, {
  UserPreferenceServiceType,
} from "./userPreferenceService";
import tocService, { TocServiceType } from "./tocService";
import dashboardService, { DashboardServiceType } from "./dashboardService";
import consolidationService, {
  ConsolidationServiceType,
} from "./consolidationService";
import piiWhitelistServices, {
  PiiWhitelistServiceType,
} from "./piiWhitelistService";
import piiOverrideServices, {
  PiiOverrideServiceType,
} from "./piiOverrideService";
import notificationSettingsServices, {
  NotificationSettingsServiceType,
} from "./notificationSettingsService";
import entraDirectorySyncServices, {
  EntraDirectorySyncServiceType,
} from "./entraDirectorySyncService";
import releaseNoteService, {
  ReleaseNoteServiceType,
} from "./releaseNoteService";

import { APP_ENV, AppEnv, MANAGEMENT_API_HOST } from "./appEnv";

const st_document_base_urls = {
  [AppEnv.Production]: "https://stdocumentsmarkupprod.blob.core.windows.net",
  [AppEnv.Test]: "https://stdocumentsmarkuptest.blob.core.windows.net",
  [AppEnv.Development]: "https://stdocumentsmarkupdev.blob.core.windows.net",
  [AppEnv.Local]: "https://stdocumentsmarkupdev.blob.core.windows.net",
};

// Chat citation PDFs live in the per-environment "sources" storage account
// (azurerm_storage_account.sources in iac/tf_application). Not the same
// account as the shared data-sources ingestion account, whose source PDFs
// load through links the API signs (useLegislationPdfUrl, LAR-542).
const st_sources_base_urls: Record<AppEnv, string> = {
  [AppEnv.Production]: "https://stsourcesmarkupprod.blob.core.windows.net",
  [AppEnv.Test]: "https://stsourcesmarkuptest.blob.core.windows.net",
  [AppEnv.Development]: "https://stsourcesmarkupdev.blob.core.windows.net",
  [AppEnv.Local]: "https://stsourcesmarkupdev.blob.core.windows.net",
};

const chat_api_hosts: Record<AppEnv, string> = {
  [AppEnv.Production]: "chat.lara.law",
  [AppEnv.Test]: "test.chat.lara.law",
  [AppEnv.Development]: "dev.chat.lara.law",
  [AppEnv.Local]: "localhost:8090",
};

const CHAT_API_HOST =
  chat_api_hosts[APP_ENV] ?? chat_api_hosts[AppEnv.Production];

const REST_PROTOCOL = APP_ENV === AppEnv.Local ? "http" : "https";

export const ST_DOCUMENTS_BASE_URL = st_document_base_urls[APP_ENV];

export const ST_SOURCES_BASE_URL =
  st_sources_base_urls[APP_ENV] ?? st_sources_base_urls[AppEnv.Production];

export const REST_API_BASE = `${REST_PROTOCOL}://${MANAGEMENT_API_HOST}`;
// Resolves the chat-api (FastAPI SSE) base URL. Explicit VITE_CHAT_API_BASE
// always wins (set in deployed SWA environments). Otherwise we map per
// VITE_APP_ENV so running the web-app locally against `dev`/`test`/`prod`
// targets the matching deployed chat-api instead of the local origin.
export const CHAT_API_BASE =
  import.meta.env.VITE_CHAT_API_BASE || `${REST_PROTOCOL}://${CHAT_API_HOST}`;

const API_BASE_V1 = "/api";
const WS_API_BASE = "/api";

export type AppItemRestServicesType = {
  readonly user: UserServiceType;
  readonly legislation: LegislationServiceType;
  readonly legislationFilter: LegislationFilterServiceType;
  readonly conversation: ConversationServiceType;
  readonly message: MessageServiceType;
  readonly negotiate: NetgotiateServiceType;
  readonly workspace: WorkspaceServiceType;
  readonly blobStorage: BlobStorageServiceType;
  readonly tenant: TenantServiceType;
  readonly prompt: PromptServiceType;
  readonly tag: TagServiceType;
  readonly statistic: StatisticServiceType;
  readonly comment: CommentServiceType;
  readonly lawTerm: LawTermServiceType;
  readonly group: GroupServiceType;
  readonly featureFlag: FeatureFlagServiceType;
  readonly toneOfVoice: ToneOfVoiceServiceType;
  readonly search: SearchServiceType;
  readonly userManagement: UserManagementServiceType;
  readonly documentLink: DocumentLinkServiceType;
  readonly synonymGroup: SynonymGroupServiceType;
  readonly userPreference: UserPreferenceServiceType;
  readonly dashboard: DashboardServiceType;
  readonly toc: TocServiceType;
  readonly consolidation: ConsolidationServiceType;
  readonly piiWhitelist: PiiWhitelistServiceType;
  readonly piiOverride: PiiOverrideServiceType;
  readonly notificationSettings: NotificationSettingsServiceType;
  readonly entraDirectorySync: EntraDirectorySyncServiceType;
  readonly releaseNote: ReleaseNoteServiceType;
};

export type AppRestServicesType = {
  readonly auth0Client: RestClient;
  readonly items: AppItemRestServicesType;
};

export const PATH_KEYS = {
  auth: `auth`,
  user: `user`,
  legislation: `legislation`,
  legislationFilter: `legislationfilter`,
  conversation: `conversation`,
  message: `message`,
  negotiate: `negotiate`,
  workspace: `workspace`,
  blobstorage: `blobstorage`,
  tenant: `tenant`,
  prompt: `prompt`,
  tag: `tag`,
  statistic: `statistic`,
  comment: `comment`,
  lawTerm: `lawterm`,
  group: `group`,
  featureFlag: `featureflag`,
  toneOfVoice: `toneofvoice`,
  search: `search`,
  userManagement: `usermanagement`,
  documentLink: `documentlink`,
  synonymGroup: `synonymgroup`,
  userPreference: `user/preferences`,
  toc: `toc`,
  dashboard: `dashboard`,
  consolidation: `Consolidation`,
  piiWhitelist: `piiwhitelist`,
  piiOverride: `piioverride`,
  notificationSettings: `user/me/notifications/settings`,
  releaseNote: `release-notes`,
};

export const PATHS = {
  auth: `${API_BASE_V1}/${PATH_KEYS.auth}`,
  user: `${API_BASE_V1}/${PATH_KEYS.user}`,
  legislation: `${API_BASE_V1}/${PATH_KEYS.legislation}`,
  legislationFilter: `${API_BASE_V1}/${PATH_KEYS.legislationFilter}`,
  conversation: `${API_BASE_V1}/${PATH_KEYS.conversation}`,
  message: `${API_BASE_V1}/${PATH_KEYS.conversation}`,
  negotiate: `${API_BASE_V1}/${PATH_KEYS.negotiate}`,
  workspace: `${API_BASE_V1}/${PATH_KEYS.workspace}`,
  blobstorage: `${API_BASE_V1}/${PATH_KEYS.blobstorage}`,
  tenant: `${API_BASE_V1}/${PATH_KEYS.tenant}`,
  prompt: `${API_BASE_V1}/${PATH_KEYS.prompt}`,
  tag: `${API_BASE_V1}/${PATH_KEYS.tag}`,
  statistic: `${API_BASE_V1}/${PATH_KEYS.statistic}`,
  comment: `${API_BASE_V1}/${PATH_KEYS.comment}`,
  lawTerm: `${API_BASE_V1}/${PATH_KEYS.lawTerm}`,
  group: `${API_BASE_V1}/${PATH_KEYS.group}`,
  featureFlag: `${API_BASE_V1}/${PATH_KEYS.featureFlag}`,
  toneOfVoice: `${API_BASE_V1}/${PATH_KEYS.toneOfVoice}`,
  search: `${API_BASE_V1}/${PATH_KEYS.search}`,
  userManagement: `${API_BASE_V1}/${PATH_KEYS.userManagement}`,
  documentLink: `${API_BASE_V1}/${PATH_KEYS.documentLink}`,
  synonymGroup: `${API_BASE_V1}/${PATH_KEYS.synonymGroup}`,
  userPreference: `${API_BASE_V1}/${PATH_KEYS.userPreference}`,
  dashboard: `${API_BASE_V1}/${PATH_KEYS.dashboard}`,
  toc: `${API_BASE_V1}/${PATH_KEYS.toc}`,
  consolidation: `${API_BASE_V1}/${PATH_KEYS.consolidation}`,
  piiWhitelist: `${API_BASE_V1}/${PATH_KEYS.piiWhitelist}`,
  piiOverride: `${API_BASE_V1}/${PATH_KEYS.piiOverride}`,
  notificationSettings: `${API_BASE_V1}/${PATH_KEYS.notificationSettings}`,
  releaseNote: `${API_BASE_V1}/${PATH_KEYS.releaseNote}`,
};

export const WEBSOCKET_PATHS = {
  conversation: `${WS_API_BASE}/${PATH_KEYS.conversation}`,
};

const appItemServices = (client: RestClient): AppItemRestServicesType => {
  const user = userServices(client, PATHS.user);
  const legislation = legislationServices(client, PATHS.legislation);
  const legislationFilter = legislationFilterServices(
    client,
    PATHS.legislationFilter
  );
  const conversation = conversationServices(client, PATHS.conversation);
  const message = messageServices(client, PATHS.message);
  const negotiate = negotiateServices(client, PATHS.negotiate);
  const workspace = workspaceServices(client, PATHS.workspace);
  const comment = commentServices(client, PATHS.comment);
  const blobStorage = blobStorageServices(client, PATHS.blobstorage);
  const tenant = tenantServices(client, PATHS.tenant);
  const prompt = promptServices(client, PATHS.prompt);
  const tag = tagServices(client, PATHS.tag);
  const statistic = statisticServices(client, PATHS.statistic);
  const lawTerm = lawTermServices(client, PATHS.lawTerm);
  const group = groupServices(client, PATHS.group);
  const featureFlag = featureFlagServices(client, PATHS.featureFlag);
  const toneOfVoice = toneOfVoiceService(client, PATHS.toneOfVoice);
  const search = searchServices(client, PATHS.search);
  const userManagement = userManagementServices(client, PATHS.userManagement);
  const documentLink = documentLinkService(client, PATHS.documentLink);
  const synonymGroup = synonymGroupServices(client, PATHS.synonymGroup);
  const userPreference = userPreferenceServices(client, PATHS.userPreference);
  const toc = tocService(client, PATHS.toc);
  const dashboard = dashboardService(client, PATHS.dashboard);
  const consolidation = consolidationService(client, PATHS.consolidation);
  const piiWhitelist = piiWhitelistServices(client, PATHS.piiWhitelist);
  const piiOverride = piiOverrideServices(client, PATHS.piiOverride);
  const notificationSettings = notificationSettingsServices(
    client,
    PATHS.notificationSettings
  );
  const entraDirectorySync = entraDirectorySyncServices(client, PATHS.tenant);
  const releaseNote = releaseNoteService(client, PATHS.releaseNote);

  return {
    user,
    legislation,
    legislationFilter,
    conversation,
    message,
    negotiate,
    workspace,
    comment,
    blobStorage,
    tenant,
    prompt,
    tag,
    statistic,
    lawTerm,
    group,
    featureFlag,
    toneOfVoice,
    search,
    userManagement,
    documentLink,
    synonymGroup,
    userPreference,
    toc,
    dashboard,
    consolidation,
    piiWhitelist,
    piiOverride,
    notificationSettings,
    entraDirectorySync,
    releaseNote,
  };
};

export const appServices = (
  getAccessTokenSilently: () => Promise<string>,
  isAuthenticated: () => boolean
): AppRestServicesType => {
  const auth0Client = restClientAuth0(
    REST_API_BASE,
    getAccessTokenSilently,
    isAuthenticated
  );
  const items = appItemServices(auth0Client);
  return {
    auth0Client,
    items,
  };
};
